import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { toast } from 'react-toastify'
import UserPicker from './UserPicker'
import {
  assignTask,
  updateApprovers,
  updateTask,
  assignExternalUser,
  removeExternalAssignee,
} from 'services/taskService'
import { listContractors } from 'services/contractorService'
import { useSeriesScope } from 'features/taskManagement/useSeriesScope'
import { hideEmail } from 'utils/user'

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

const TaskPeopleModal = ({ task, bpId, onClose, onSaved }) => {
  const { promptNode: seriesScopePrompt, requestScope: requestSeriesScope } = useSeriesScope(task)
  const [assigneeIds, setAssigneeIds] = useState(task?.assignees?.map(a => a.id) || [])
  const [approverIds, setApproverIds] = useState(task?.approvers?.map(a => a.id) || [])
  const [contractorId, setContractorId] = useState(task?.contractor?.id || task?.contractorId || null)

  const [contractors, setContractors] = useState([])
  const [contractorLoading, setContractorLoading] = useState(false)
  const [contractorSearch, setContractorSearch] = useState('')

  const [externals, setExternals] = useState(task?.externalAssignees || [])
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [externalBusy, setExternalBusy] = useState(false)

  const [saving, setSaving] = useState(false)

  const initial = useMemo(() => ({
    assignees: (task?.assignees?.map(a => a.id) || []).slice().sort().join(','),
    approvers: (task?.approvers?.map(a => a.id) || []).slice().sort().join(','),
    contractor: task?.contractor?.id || task?.contractorId || null,
  }), [task])

  useEffect(() => {
    setContractorLoading(true)
    listContractors()
      .then(res => setContractors(res?.data?.data?.contractors || []))
      .catch(() => setContractors([]))
      .finally(() => setContractorLoading(false))
  }, [])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const overlapNames = useMemo(() => {
    const approverSet = new Set(approverIds)
    return [...(task?.assignees || []), ...(task?.approvers || [])]
      .filter(u => assigneeIds.includes(u.id) && approverSet.has(u.id))
      .map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim() || (u.email ? hideEmail(u.email) : 'This person'))
      .filter((v, i, a) => a.indexOf(v) === i)
  }, [assigneeIds, approverIds, task])

  const filteredContractors = useMemo(() => {
    const q = contractorSearch.trim().toLowerCase()
    if (!q) return contractors
    return contractors.filter(c => [c.name, c.company, c.trade].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
  }, [contractors, contractorSearch])

  const inviteExternal = async () => {
    const e = email.trim()
    if (!isEmail(e)) { toast.info('Enter a valid email address.'); return }
    if (externals.some(x => (x.email || '').toLowerCase() === e.toLowerCase())) { toast.info('That person is already invited.'); return }
    const scope = await requestSeriesScope()
    if (scope === null) return
    setExternalBusy(true)
    try {
      const res = await assignExternalUser({ taskId: task.id, email: e, name: name.trim() || null, scope })
      const updated = res?.data?.data?.externalAssignees
      setExternals(Array.isArray(updated) ? updated : [...externals, { id: e, email: e, name: name.trim() || null }])
      setEmail('')
      setName('')
      onSaved?.()
    } catch (err) {
      toast.error('Could not send the invite. Please try again.')
    } finally {
      setExternalBusy(false)
    }
  }

  const removeExternal = async (assignee) => {
    const scope = await requestSeriesScope()
    if (scope === null) return
    setExternalBusy(true)
    try {
      await removeExternalAssignee({ taskId: task.id, externalAssigneeId: assignee.id, scope })
      setExternals(externals.filter(x => x.id !== assignee.id))
      onSaved?.()
    } catch (err) {
      toast.error('Could not remove that person. Please try again.')
    } finally {
      setExternalBusy(false)
    }
  }

  const save = async () => {
    const assigneesChanged = assigneeIds.slice().sort().join(',') !== initial.assignees
    const approversChanged = approverIds.slice().sort().join(',') !== initial.approvers
    const contractorChanged = (contractorId || null) !== initial.contractor
    if (!assigneesChanged && !approversChanged && !contractorChanged) {
      onClose?.()
      return
    }
    const scope = await requestSeriesScope()
    if (scope === null) return
    setSaving(true)
    try {
      const jobs = []
      if (assigneesChanged) {
        jobs.push(assignTask({ taskId: task.id, assigneeIds, scope }))
      }
      if (approversChanged) {
        jobs.push(updateApprovers({ taskId: task.id, approverIds, scope }))
      }
      if (contractorChanged) {
        jobs.push(updateTask({ id: task.id, contractorId: contractorId || null, scope }))
      }
      if (jobs.length) await Promise.all(jobs)
      toast.success('People updated.')
      onSaved?.()
      onClose?.()
    } catch (err) {
      toast.error('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!task?.id) return null

  return (
    <>
      {createPortal(
    <Overlay onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onClose() }}>
      <Card role='dialog' aria-label='Manage people' onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        <Header>
          <div>
            <HeaderTitle>Manage people</HeaderTitle>
            <HeaderSub>{task.title}</HeaderSub>
          </div>
          <CloseBtn type='button' onClick={onClose} aria-label='Close'><CloseRoundedIcon /></CloseBtn>
        </Header>

        <Body>
          <Section>
            <UserPicker
              label='Assignees'
              value={assigneeIds}
              onChange={setAssigneeIds}
              buildingPassportId={bpId}
              placeholder='Search people to assign…'
              initialUsers={task.assignees || []}
            />
          </Section>

          <Section>
            <UserPicker
              label='Approvers'
              value={approverIds}
              onChange={setApproverIds}
              buildingPassportId={bpId}
              placeholder='Search people to approve…'
              initialUsers={task.approvers || []}
            />
            {overlapNames.length > 0 && (
              <WarnNote>
                {overlapNames.join(', ')} {overlapNames.length === 1 ? 'is' : 'are'} also an assignee, so they would approve their own work. You can still save.
              </WarnNote>
            )}
          </Section>

          <Section>
            <SectionLabel><EngineeringOutlinedIcon style={{ fontSize: 16 }} /> Contractor</SectionLabel>
            <SearchRow>
              <SearchRoundedIcon style={{ fontSize: 16, color: '#9aa0ab' }} />
              <SearchInput
                placeholder='Search contractors…'
                value={contractorSearch}
                onChange={e => setContractorSearch(e.target.value)}
              />
            </SearchRow>
            <PickList>
              <PickItem type='button' $checked={!contractorId} onClick={() => setContractorId(null)}>
                <Tick $on={!contractorId}>{!contractorId ? '✓' : ''}</Tick>
                <span>No contractor</span>
              </PickItem>
              {contractorLoading ? (
                <>
                  <SkeletonLine />
                  <SkeletonLine />
                  <SkeletonLine />
                </>
              ) : filteredContractors.length === 0 ? (
                <EmptyRow>No contractors found.</EmptyRow>
              ) : filteredContractors.map(c => (
                <PickItem key={c.id} type='button' $checked={contractorId === c.id} onClick={() => setContractorId(c.id)}>
                  <Tick $on={contractorId === c.id}>{contractorId === c.id ? '✓' : ''}</Tick>
                  <PickText>
                    <PickName>{c.name}</PickName>
                    {(c.company || c.trade) && <PickMeta>{[c.company, c.trade].filter(Boolean).join(' · ')}</PickMeta>}
                  </PickText>
                </PickItem>
              ))}
            </PickList>
          </Section>

          <Section>
            <SectionLabel><MailOutlineIcon style={{ fontSize: 16 }} /> External people</SectionLabel>
            {externals.length > 0 && (
              <ExternalList>
                {externals.map(x => (
                  <ExternalRow key={x.id}>
                    <ExternalText>
                      <PickName>{x.name || (x.email ? hideEmail(x.email) : 'External')}</PickName>
                      {x.email && <PickMeta>{hideEmail(x.email)}</PickMeta>}
                    </ExternalText>
                    <RemoveBtn type='button' disabled={externalBusy} onClick={() => removeExternal(x)} aria-label='Remove'>
                      <CloseRoundedIcon style={{ fontSize: 15 }} />
                    </RemoveBtn>
                  </ExternalRow>
                ))}
              </ExternalList>
            )}
            <InviteRow>
              <InviteInput placeholder='name@company.com' value={email} onChange={e => setEmail(e.target.value)} />
              <InviteInput placeholder='Name (optional)' value={name} onChange={e => setName(e.target.value)} />
              <InviteBtn type='button' disabled={externalBusy} onClick={inviteExternal}>Invite</InviteBtn>
            </InviteRow>
          </Section>
        </Body>

        <Footer>
          <CancelBtn type='button' onClick={onClose} disabled={saving}>Cancel</CancelBtn>
          <SaveBtn type='button' onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</SaveBtn>
        </Footer>
      </Card>
    </Overlay>,
        document.body
      )}
      {seriesScopePrompt}
    </>
  )
}

export default TaskPeopleModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100010;
  background: rgba(15, 18, 34, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`
const Card = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px -28px rgba(12, 18, 41, 0.45);
  overflow: hidden;
`
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #eef0f3;
`
const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #1c2029;
`
const HeaderSub = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: #8a90a0;
`
const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: #8a90a0;
  cursor: pointer;
  display: inline-flex;
  padding: 4px;
  border-radius: 8px;
  &:hover { background: #f2f4f7; color: #1c2029; }
`
const Body = styled.div`
  padding: 18px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 9px;
`
const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font: inherit;
  font-size: 13px;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
`
const PickList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 200px;
  overflow-y: auto;
`
const PickItem = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid ${({ $checked }) => ($checked ? '#bfe6ea' : 'transparent')};
  background: ${({ $checked }) => ($checked ? '#f2fbfb' : 'transparent')};
  &:hover { background: ${({ $checked }) => ($checked ? '#eaf8f9' : '#f7f8fa')}; }
`
const Tick = styled.span`
  flex: 0 0 auto;
  width: 19px;
  height: 19px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`
const PickText = styled.div`flex: 1; min-width: 0;`
const PickName = styled.div`font-size: 13px; font-weight: 600; color: #1c2029; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const PickMeta = styled.div`font-size: 11px; color: #8a90a0;`
const EmptyRow = styled.div`font-size: 12.5px; color: #8a90a0; padding: 10px 4px;`
const SkeletonLine = styled.div`
  height: 34px;
  border-radius: 9px;
  background: #f1f3f6;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
`
const ExternalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const ExternalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 10px;
  border: 1px solid #eef0f3;
  border-radius: 9px;
`
const ExternalText = styled.div`min-width: 0;`
const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: #8a90a0;
  cursor: pointer;
  display: inline-flex;
  padding: 3px;
  border-radius: 6px;
  &:hover { background: #fdecea; color: #b00020; }
  &:disabled { opacity: 0.5; cursor: default; }
`
const InviteRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`
const InviteInput = styled.input`
  flex: 1;
  min-width: 140px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 9px;
  font: inherit;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.secondary}; }
`
const InviteBtn = styled.button`
  padding: 8px 15px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 9px;
  background: #fff;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #e6f7f9; }
  &:disabled { opacity: 0.6; cursor: default; }
`
const WarnNote = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: #d97706;
  background: #fef3c7;
  border-radius: 8px;
  padding: 8px 10px;
`
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #eef0f3;
`
const CancelBtn = styled.button`
  padding: 9px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
const SaveBtn = styled.button`
  padding: 9px 20px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
